import type { ReactNode } from 'react'
import React, { useRef } from 'react'
import { css } from '@emotion/core'
import { Elevation, Portal } from '@fs/zion-ui/quarks-authorized-use-only'
import { colors } from '@fs/zion-ui'
import useAppContentScrollerWidth from '../../../helpers/useAppContentScollerWidth'
import useStickyHeaderTransform from '../../../helpers/useStickyHeaderTransform'

const personHeaderStickyCss = css`
  top: 0;
  z-index: 1;
  transition: all 0.3s ease-in-out;
  position: fixed;
  transform: translateY(-120%);
  background-color: ${colors.gray00};
  width: 100%;

  @media print {
    display: none;
  }

  & p {
    padding: 0;
  }

  /* Makes Ellipsis work on children */
  & > * {
    min-width: 0;
  }
`

type Props = {
  children: ReactNode
}

export default function StickyHeader({ children }: Props): JSX.Element {
  const headerRef = useRef<HTMLDivElement>(null)
  const aboveHeaderRef = useRef<HTMLDivElement>(null)

  useAppContentScrollerWidth((appWidthNumber) => {
    if (headerRef.current) headerRef.current.style.width = `${appWidthNumber}px`
  })

  useStickyHeaderTransform({ refOfPointToShow: aboveHeaderRef, stickyRef: headerRef })

  return (
    <>
      {/* This div acts as a point of reference for the IntersectionObserver used to manage sticky and elevation styles. */}
      <div ref={aboveHeaderRef} />
      {/* To make sure that the sticky header is always above the content, we use a Portal to render it outside the normal DOM flow. It is still rendered below DialogOverlays. Resolves TREEWEB-9048 */}
      <Portal>
        <div css={personHeaderStickyCss} ref={headerRef}>
          <Elevation z={4}>{children}</Elevation>
        </div>
      </Portal>
    </>
  )
}
